<template>
    <transition name="slide">
        <div
            v-if="visible"
            :class="[
                'side-panel',
                size,
                {
                    'align-left': alignLeft,
                    'height-locked': noScroll,
                },
            ]"
            ref="panel"
            :style="panelStyle"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="title ? titleId : undefined"
            :aria-label="title ? undefined : 'Side panel'"
            tabindex="-1"
            @keydown="trapFocus($event, $refs.panel)"
        >
            <div
                v-if="shouldRenderChildPanel"
                class="side-panel-child-parent-scrim"
                @click.stop="closeChildPanelFromParent"
            ></div>

            <div class="panel-head gap-3" :inert="shouldRenderChildPanel">
                <slot name="top-left"></slot>
                <h2 v-if="title" :id="titleId" class="panel-title">
                    {{ title }}
                </h2>
                <slot name="title-badge"></slot>
                <button
                    v-if="closeVisible"
                    type="button"
                    class="close-btn icon-btn icon-btn-white"
                    aria-label="Close panel"
                    @click="closePanelFromButton"
                >
                    <i class="fi fi-x"></i>
                </button>
            </div>

            <div class="panel-contents-wrapper" :inert="shouldRenderChildPanel">
                <div>
                    <slot name="header"></slot>
                </div>

                <div
                    class="panel-body"
                    :style="bodyStyle"
                    :class="{ 'height-locked': noScroll }"
                >
                    <slot></slot>
                </div>

                <div>
                    <slot name="footer"></slot>
                </div>
            </div>
        </div>
    </transition>

    <transition name="child-panel-slide">
        <div
            v-if="shouldRenderChildPanel"
            :class="[
                'side-panel-child',
                childPanelSize,
                `side-panel-child--${childPanelMode}`,
            ]"
            ref="childPanel"
            tabindex="-1"
            :style="computedChildPanelStyle"
            role="dialog"
            aria-modal="true"
            :aria-labelledby="childPanelTitle ? childTitleId : undefined"
            :aria-label="childPanelTitle ? undefined : 'Child panel'"
            @keydown="trapFocus($event, $refs.childPanel)"
        >
            <div class="panel-head gap-3">
                <slot name="child-panel-top-left"></slot>
                <h2
                    v-if="childPanelTitle"
                    :id="childTitleId"
                    class="panel-title"
                >
                    {{ childPanelTitle }}
                </h2>
                <button
                    v-if="childPanelCloseVisible"
                    type="button"
                    class="close-btn icon-btn icon-btn-white"
                    aria-label="Close child panel"
                    @click="closeChildPanelFromButton"
                >
                    <i class="fi fi-x"></i>
                </button>
            </div>

            <div class="panel-contents-wrapper">
                <div>
                    <slot name="child-panel-header"></slot>
                </div>

                <div class="panel-body" :style="childPanelBodyStyle">
                    <slot name="child-panel"></slot>
                </div>

                <div>
                    <slot name="child-panel-footer"></slot>
                </div>
            </div>
        </div>
    </transition>

    <!-- Overlay div to close the panel when clicking outside -->
    <Teleport to="body">
        <transition name="fade">
            <div
                v-if="visible && !hideOverlay"
                class="overlay"
                @click="closePanelFromOverlay"
            ></div>
        </transition>
    </Teleport>

    <ConfirmModal
        :visible="isModalVisible"
        :title="resolvedCloseConfirmation.title"
        :icon="resolvedCloseConfirmation.iconClass"
        :cancel-button-text="resolvedCloseConfirmation.cancelText"
        :confirm-button-text="resolvedCloseConfirmation.confirmText"
        :cancel-button-class="resolvedCloseConfirmation.cancelButtonClass"
        :confirm-button-class="resolvedCloseConfirmation.confirmButtonClass"
        @close="closeModal"
        @confirm="closePanel"
    >
        {{ resolvedCloseConfirmation.message }}
    </ConfirmModal>

    <ConfirmModal
        :visible="isChildModalVisible"
        :title="resolvedChildPanelCloseConfirmation.title"
        :icon="resolvedChildPanelCloseConfirmation.iconClass"
        :cancel-button-text="resolvedChildPanelCloseConfirmation.cancelText"
        :confirm-button-text="resolvedChildPanelCloseConfirmation.confirmText"
        :cancel-button-class="
            resolvedChildPanelCloseConfirmation.cancelButtonClass
        "
        :confirm-button-class="
            resolvedChildPanelCloseConfirmation.confirmButtonClass
        "
        @close="closeChildModal"
        @confirm="closeChildPanel"
    >
        {{ resolvedChildPanelCloseConfirmation.message }}
    </ConfirmModal>
</template>

<script>
import ConfirmModal from './ConfirmModal.vue';

const PANEL_WIDTH_VARIABLES = {
    'panel-sm': '--side-panel-width-sm',
    'panel-md': '--side-panel-width-md',
    'panel-lg': '--side-panel-width-lg',
    'panel-xl': '--side-panel-width-xl',
};

const SIDE_PANEL_INSET = 'var(--side-panel-inset)';
const CHILD_PANEL_DOCK_GAP = 'var(--side-panel-child-dock-gap)';
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
let nextSidePanelId = 0;
const DEFAULT_CLOSE_CONFIRMATION = {
    iconClass: 'fi-alert-circle',
    title: 'Exit?',
    message: 'Are you sure you want to exit? All progress will be lost.',
    cancelText: 'No',
    confirmText: 'Exit',
    cancelButtonClass: 'btn btn-desktop-lg btn-white me-2',
    confirmButtonClass: 'btn btn-desktop-lg btn-primary',
};

export default {
    name: 'SidePanel',
    components: {
        ConfirmModal,
    },
    props: {
        // toggles panel visibility
        visible: {
            type: Boolean,
            required: true,
        },
        // panel width: sm-200 px, md-400 px, lg-800 px, full-100%
        size: {
            type: String,
            default: 'panel-md',
            validator(value) {
                return [
                    'panel-sm',
                    'panel-md',
                    'panel-lg',
                    'panel-xl',
                    'panel-full',
                ].includes(value);
            },
        },
        // The displayed title on the panel
        title: {
            type: String,
            default: null,
        },
        // Exit button displayed if true
        closeVisible: {
            type: Boolean,
            default: true,
        },
        // If true, aligns panel on left side of page
        alignLeft: {
            type: Boolean,
            default: false,
        },
        // If true, lock height from overflowing
        noScroll: {
            type: Boolean,
            default: false,
        },
        bodyStyle: {
            type: String,
            default: '',
        },
        panelStyle: {
            type: [String, Object],
            default: '',
        },
        confirmClose: {
            type: [Boolean, Function],
            default: false,
        },
        closeConfirmation: {
            type: Object,
            default: () => ({}),
        },
        hideOverlay: {
            type: Boolean,
            default: false,
        },
        // Optional secondary surface rendered beside this panel when space allows.
        childPanelVisible: {
            type: Boolean,
            default: false,
        },
        childPanelTitle: {
            type: String,
            default: null,
        },
        childPanelSize: {
            type: String,
            default: 'panel-md',
            validator(value) {
                return [
                    'panel-sm',
                    'panel-md',
                    'panel-lg',
                    'panel-xl',
                ].includes(value);
            },
        },
        childPanelCloseVisible: {
            type: Boolean,
            default: true,
        },
        childPanelConfirmClose: {
            type: [Boolean, Function],
            default: false,
        },
        childPanelCloseConfirmation: {
            type: Object,
            default: () => ({}),
        },
        childPanelBodyStyle: {
            type: String,
            default: '',
        },
        childPanelPanelStyle: {
            type: [String, Object],
            default: '',
        },
    },
    data() {
        const panelId = ++nextSidePanelId;
        return {
            titleId: `side-panel-title-${panelId}`,
            childTitleId: `side-panel-child-title-${panelId}`,
            isModalVisible: false,
            isChildModalVisible: false,
            closeSource: null, // 'button' or 'overlay'
            childCloseSource: null,
            previousFocus: null,
        };
    },
    computed: {
        shouldRenderChildPanel() {
            return (
                this.visible &&
                this.childPanelVisible &&
                Boolean(this.$slots['child-panel'])
            );
        },
        childPanelMode() {
            return this.isChildPanelDocked ? 'docked' : 'overlay';
        },
        isChildPanelDocked() {
            if (
                !this.shouldRenderChildPanel ||
                this.$isMobile ||
                this.alignLeft
            ) {
                return false;
            }

            const mainPanelWidth = this.getPanelWidth(this.size);
            const childPanelWidth = this.getPanelWidth(this.childPanelSize);
            const sidePanelInset = this.getCssPixelValue('--side-panel-inset');
            const childPanelDockGap = this.getCssPixelValue(
                '--side-panel-child-dock-gap'
            );
            if (
                !mainPanelWidth ||
                !childPanelWidth ||
                !sidePanelInset ||
                !childPanelDockGap
            ) {
                return false;
            }

            return (
                this.$windowWidth >=
                mainPanelWidth +
                    childPanelWidth +
                    sidePanelInset +
                    childPanelDockGap
            );
        },
        computedChildPanelStyle() {
            const baseStyle = this.isChildPanelDocked
                ? {
                      right: `calc(${SIDE_PANEL_INSET} + var(${
                          PANEL_WIDTH_VARIABLES[this.size]
                      }) + ${CHILD_PANEL_DOCK_GAP})`,
                  }
                : {};

            if (typeof this.childPanelPanelStyle === 'string') {
                const baseStyleString = Object.entries(baseStyle)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join('; ');
                return [baseStyleString, this.childPanelPanelStyle]
                    .filter(Boolean)
                    .join('; ');
            }

            return {
                ...baseStyle,
                ...this.childPanelPanelStyle,
            };
        },
        resolvedCloseConfirmation() {
            return {
                ...DEFAULT_CLOSE_CONFIRMATION,
                ...this.closeConfirmation,
            };
        },
        resolvedChildPanelCloseConfirmation() {
            return {
                ...DEFAULT_CLOSE_CONFIRMATION,
                ...this.childPanelCloseConfirmation,
            };
        },
    },
    watch: {
        visible(newValue) {
            if (newValue) {
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
                this.isChildModalVisible = false;
            }
        },
        shouldRenderChildPanel(value) {
            if (value) {
                this.focusChildPanel();
            } else {
                this.restorePreviousFocus();
            }
        },
    },
    emits: ['close', 'child-panel-close'],
    methods: {
        trapFocus(event, container) {
            if (event.key !== 'Tab' || !container) return;
            const focusable = [
                ...container.querySelectorAll(FOCUSABLE_SELECTOR),
            ].filter(
                (element) =>
                    !element.hidden &&
                    element.getAttribute('aria-hidden') !== 'true' &&
                    element.offsetParent !== null
            );
            if (!focusable.length) {
                event.preventDefault();
                container.focus();
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const activeElement = document.activeElement;
            if (
                event.shiftKey &&
                (activeElement === first || !container.contains(activeElement))
            ) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },
        closePanelFromButton() {
            this.closeSource = 'button';
            this.closePanel();
        },
        closePanelFromOverlay() {
            this.closeSource = 'overlay';
            this.closePanel();
        },
        closePanel() {
            // Support both boolean and function for confirmClose
            const shouldConfirm =
                typeof this.confirmClose === 'function'
                    ? this.confirmClose()
                    : this.confirmClose;

            if (shouldConfirm && !this.isModalVisible) {
                this.isModalVisible = true;
            } else {
                // Store the source before closing modal
                const source = this.closeSource;
                this.isModalVisible = false;
                this.$emit('close', source);
                this.closeSource = null;
            }
        },
        closeModal() {
            this.isModalVisible = false;
            this.closeSource = null;
        },
        closeChildPanelFromButton() {
            this.childCloseSource = 'button';
            this.closeChildPanel();
        },
        closeChildPanelFromParent() {
            this.childCloseSource = 'parent';
            this.closeChildPanel();
        },
        closeChildPanel() {
            const shouldConfirm =
                typeof this.childPanelConfirmClose === 'function'
                    ? this.childPanelConfirmClose()
                    : this.childPanelConfirmClose;

            if (shouldConfirm && !this.isChildModalVisible) {
                this.isChildModalVisible = true;
            } else {
                const source = this.childCloseSource;
                this.isChildModalVisible = false;
                this.$emit('child-panel-close', source);
                this.childCloseSource = null;
            }
        },
        closeChildModal() {
            this.isChildModalVisible = false;
            this.childCloseSource = null;
        },
        focusChildPanel() {
            this.previousFocus = document.activeElement;
            this.$nextTick(() => {
                this.$refs.childPanel?.focus?.();
            });
        },
        restorePreviousFocus() {
            this.$nextTick(() => {
                this.previousFocus?.focus?.();
                this.previousFocus = null;
            });
        },
        getPanelWidth(size) {
            const variableName = PANEL_WIDTH_VARIABLES[size];
            if (!variableName) return null;

            return this.getCssPixelValue(variableName);
        },
        getCssPixelValue(variableName) {
            const rawValue = getComputedStyle(document.documentElement)
                .getPropertyValue(variableName)
                .trim();

            if (rawValue.endsWith('px')) return parseFloat(rawValue);

            if (rawValue.endsWith('rem')) {
                return (
                    parseFloat(rawValue) *
                    parseFloat(
                        getComputedStyle(document.documentElement).fontSize
                    )
                );
            }

            const probe = document.createElement('div');
            probe.style.position = 'absolute';
            probe.style.visibility = 'hidden';
            probe.style.width = `var(${variableName})`;
            document.body.appendChild(probe);
            const width = probe.getBoundingClientRect().width;
            probe.remove();

            return Number.isFinite(width) ? width : null;
        },
    },
};
</script>

<style scoped>
.side-panel {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: var(--side-panel-inset);
    right: var(--side-panel-inset);
    bottom: var(--side-panel-inset);
    width: 100%;
    border-radius: var(--radius-lg);
    max-height: 100%;
    background-color: white;
    z-index: var(--z-index-side-panel);
    overflow-y: hidden;
}

.side-panel-child-parent-scrim {
    position: absolute;
    inset: 0;
    z-index: 5;
    background: rgba(64, 89, 81, 0.12);
    cursor: pointer;
}

.side-panel-child {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: var(--side-panel-inset);
    right: var(--side-panel-inset);
    bottom: var(--side-panel-inset);
    width: 100%;
    border-radius: var(--radius-lg);
    max-height: 100%;
    background-color: white;
    z-index: calc(var(--z-index-side-panel) + 1);
    overflow-y: hidden;
}

.side-panel-child--overlay {
    top: 6rem;
    bottom: 2rem;
    border: 0.0625rem solid var(--neutral-600);
    box-shadow: 0 1rem 2.5rem rgba(45, 45, 45, 0.18);
}

.panel-head {
    display: flex;
    align-items: flex-end;
    position: relative;
    background-color: var(--neutral-500);
    padding: var(--space-5) var(--space-5);
    padding-right: 3.3rem;
    min-height: 5rem;
    height: auto;
    max-height: 7rem;
    overflow-y: hidden;
}

.panel-title {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0;
    align-self: flex-end;
}

.panel-body {
    overflow-x: hidden;
    flex-grow: 1;
    overflow-y: auto;
    padding-right: 0.5rem;
    min-height: 0;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

.close-btn {
    position: absolute;
    top: 1rem;
    right: 1rem;
}

.panel-contents-wrapper {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    padding: var(--space-6);
    overflow: hidden;
    min-height: 0;
}

.height-locked {
    overflow-y: hidden;
}

.side-panel.align-left {
    left: var(--side-panel-inset);
    right: auto;
}

.side-panel.panel-sm {
    max-width: min(
        var(--side-panel-width-sm),
        calc(100% - 2 * var(--side-panel-inset))
    );
}

.side-panel-child.panel-sm {
    max-width: min(
        var(--side-panel-width-sm),
        calc(100% - 2 * var(--side-panel-inset))
    );
}

.side-panel.panel-md {
    max-width: min(
        var(--side-panel-width-md),
        calc(100% - 2 * var(--side-panel-inset))
    );
}

.side-panel-child.panel-md {
    max-width: min(
        var(--side-panel-width-md),
        calc(100% - 2 * var(--side-panel-inset))
    );
}

.side-panel.panel-lg {
    max-width: min(
        var(--side-panel-width-lg),
        calc(100% - 2 * var(--side-panel-inset))
    );
}

.side-panel-child.panel-lg {
    max-width: min(
        var(--side-panel-width-lg),
        calc(100% - 2 * var(--side-panel-inset))
    );
}

.side-panel.panel-xl {
    max-width: min(
        var(--side-panel-width-xl),
        calc(100% - 2 * var(--side-panel-inset))
    );
}

.side-panel-child.panel-xl {
    max-width: min(
        var(--side-panel-width-xl),
        calc(100% - 2 * var(--side-panel-inset))
    );
}

.side-panel.panel-full {
    width: calc(100% - 2 * var(--side-panel-inset));
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease-in-out;
}

.slide-enter-from,
.slide-leave-to {
    /* Panel off the screen (on the right) */
    transform: translateX(100%);
}

.slide-enter-from.align-left,
.slide-leave-to.align-left {
    /* Panel off the screen (on the left) */
    transform: translateX(-100%);
}

.child-panel-slide-enter-active,
.child-panel-slide-leave-active {
    transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
}

.child-panel-slide-enter-from,
.child-panel-slide-leave-to {
    opacity: 0;
    transform: translateX(100%);
}

.child-panel-slide-enter-from.side-panel-child--docked,
.child-panel-slide-leave-to.side-panel-child--docked {
    transform: translateX(var(--side-panel-inset));
}

/* Fade transition for overlay */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Overlay to cover the rest of the page */
.overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    /* semi-transparent overlay */
    background-color: rgba(64, 89, 81, 0.25);
    /* one level below the panel */
    z-index: var(--z-index-overlay);
}

/* Scrollbar behavior */
.panel-body::-webkit-scrollbar {
    width: 3px;
    opacity: 0;
    transition: all 0.3s ease;
}

.panel-body:hover::-webkit-scrollbar {
    opacity: 1;
}

.panel-body::-webkit-scrollbar-track {
    background: var(--neutral-450);
    border-radius: var(--radius-sm);
}

.panel-body::-webkit-scrollbar-thumb {
    background: var(--neutral-600);
    border-radius: var(--radius-sm);
}

.panel-body::-webkit-scrollbar-thumb:hover {
    background: var(--neutral-700);
}

@media only screen and (max-width: 767.98px) {
    .side-panel {
        top: var(--space-2);
        right: var(--space-2);
        bottom: var(--space-2);
    }

    .side-panel-child {
        top: var(--space-2);
        right: var(--space-2);
        bottom: var(--space-2);
    }

    .side-panel-child--overlay {
        top: 4.75rem;
        bottom: var(--space-3);
    }

    .side-panel.align-left {
        left: var(--space-2);
    }

    .side-panel.panel-sm {
        max-width: min(
            var(--side-panel-width-sm),
            calc(100% - 2 * var(--space-2))
        );
    }

    .side-panel-child.panel-sm {
        max-width: min(
            var(--side-panel-width-sm),
            calc(100% - 2 * var(--space-2))
        );
    }

    .side-panel.panel-md {
        max-width: min(
            var(--side-panel-width-md),
            calc(100% - 2 * var(--space-2))
        );
    }

    .side-panel-child.panel-md {
        max-width: min(
            var(--side-panel-width-md),
            calc(100% - 2 * var(--space-2))
        );
    }

    .side-panel.panel-lg {
        max-width: min(
            var(--side-panel-width-lg),
            calc(100% - 2 * var(--space-2))
        );
    }

    .side-panel-child.panel-lg {
        max-width: min(
            var(--side-panel-width-lg),
            calc(100% - 2 * var(--space-2))
        );
    }

    .side-panel.panel-xl {
        max-width: min(
            var(--side-panel-width-xl),
            calc(100% - 2 * var(--space-2))
        );
    }

    .side-panel-child.panel-xl {
        max-width: min(
            var(--side-panel-width-xl),
            calc(100% - 2 * var(--space-2))
        );
    }

    .side-panel.panel-full {
        width: calc(100% - 2 * var(--space-2));
    }

    .panel-contents-wrapper {
        padding: var(--space-4);
    }

    .panel-head {
        padding: var(--space-4);
        padding-top: var(--space-3);
        padding-right: 4rem;
        min-height: 3.75rem;
    }

    .close-btn {
        top: var(--space-3);
        right: var(--space-3);
    }
}
</style>
